/**
 * Google Integration Service
 * Gerencia autenticação OAuth 2.0 com Google, integração com Gmail API (envio de convites)
 * e Google Agenda API (sincronização de compromissos e atividades).
 */

export interface GoogleUser {
  email: string;
  name: string;
  picture?: string;
  accessToken: string;
  connectedAt: string;
  expiresAt: number;
}

export interface SendInviteParams {
  to: string;
  recipientName: string;
  roleDeals?: "admin" | "user";
  roleGlobal?: "admin" | "user";
  ownerName?: string;
  ownerEmail?: string;
  loginUrl?: string;
  tempPassword?: string;
}

export interface CalendarEventParams {
  taskId?: string | number;
  salesId?: string | number | null;
  eventId?: string;
  title: string;
  description?: string;
  notes?: string | null;
  type?: string;
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  location?: string;
  attendees?: string[];
  createMeetLink?: boolean;
}

import { getSupabaseClient } from "../supabase/supabase";

export interface GCalActivityTypeConfig {
  value: string;
  label: string;
  description: string;
}

export const GCAL_SYNC_ACTIVITY_OPTIONS: GCalActivityTypeConfig[] = [
  {
    value: "chamada",
    label: "Chamada",
    description: "Ligações telefônicas e chamadas de voz",
  },
  {
    value: "reuniao",
    label: "Reunião",
    description: "Reuniões presenciais e videoconferências no Google Meet",
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    description: "Mensagens e contatos pelo WhatsApp",
  },
  {
    value: "email",
    label: "E-mail",
    description: "E-mails agendados e propostas comerciais",
  },
  {
    value: "follow-up",
    label: "Follow-up",
    description: "Retornos e acompanhamentos com o cliente",
  },
  {
    value: "visita",
    label: "Visita",
    description: "Visitas comerciais e reuniões externas no local",
  },
  {
    value: "tarefa",
    label: "Tarefa",
    description: "Tarefas operacionais, lembretes e afazeres",
  },
];

export const ALL_GCAL_SYNC_TASK_TYPES = [
  "chamada",
  "reuniao",
  "whatsapp",
  "email",
  "follow-up",
  "visita",
  "tarefa",
];

const GCAL_SYNC_TYPES_STORAGE_KEY = "dotus_crm_gcal_sync_types";

/** Normaliza o tipo de atividade para corresponder às 7 opções padrão */
export function normalizeTaskType(type?: string | null): string {
  if (!type) return "tarefa";
  const t = type.toLowerCase().trim();
  if (t === "call" || t === "chamada") return "chamada";
  if (t === "meeting" || t === "reuniao" || t === "reunião") return "reuniao";
  if (t === "whatsapp" || t === "whats" || t === "zap") return "whatsapp";
  if (t === "email" || t === "e-mail" || t === "mail") return "email";
  if (t === "follow-up" || t === "followup" || t === "follow_up")
    return "follow-up";
  if (t === "visit" || t === "visita") return "visita";
  if (t === "task" || t === "tarefa") return "tarefa";
  return t;
}

/**
 * Retorna os tipos de atividade configurados para sincronizar com o Google Calendar.
 * Por padrão, caso nunca configurado, retorna todas as 7 opções habilitadas.
 */
export function getGCalSyncTaskTypes(
  salesId?: string | number | null,
): string[] {
  try {
    const targetId = salesId ?? getCurrentLoggedInSaleId();
    const key =
      targetId != null
        ? `${GCAL_SYNC_TYPES_STORAGE_KEY}_${targetId}`
        : GCAL_SYNC_TYPES_STORAGE_KEY;
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => normalizeTaskType(item));
      }
    }
  } catch {}
  return [...ALL_GCAL_SYNC_TASK_TYPES];
}

/**
 * Salva as preferências de tipos de atividade sincronizáveis para o vendedor.
 */
export function setGCalSyncTaskTypes(
  types: string[],
  salesId?: string | number | null,
): void {
  try {
    const targetId = salesId ?? getCurrentLoggedInSaleId();
    const key =
      targetId != null
        ? `${GCAL_SYNC_TYPES_STORAGE_KEY}_${targetId}`
        : GCAL_SYNC_TYPES_STORAGE_KEY;
    const normalized = Array.from(
      new Set(types.map((item) => normalizeTaskType(item))),
    );
    localStorage.setItem(key, JSON.stringify(normalized));

    // Salva também no user_metadata do Supabase se o usuário estiver autenticado
    try {
      const client = getSupabaseClient();
      if (client?.auth) {
        client.auth
          .updateUser({
            data: { gcal_sync_types: normalized },
          })
          .catch(() => {});
      }
    } catch {}
  } catch {}
}

/**
 * Verifica se um tipo de atividade específico está autorizado para sincronizar com o Google Agenda.
 */
export function isTaskTypeAllowedForGCalSync(
  taskType?: string | null,
  salesId?: string | number | null,
): boolean {
  const allowed = getGCalSyncTaskTypes(salesId);
  const normalized = normalizeTaskType(taskType);
  return allowed.includes(normalized);
}

const STORAGE_KEY = "dotus_crm_google_user";
const GCAL_EVENTS_STORAGE_KEY = "dotus_crm_gcal_events";

/**
 * Recupera o ID do vendedor atualmente logado no CRM através do localStorage.
 */
export function getCurrentLoggedInSaleId(): string | number | null {
  try {
    const raw = localStorage.getItem("RaStore.auth.current_sale");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.id != null) return parsed.id;
    }
  } catch {}
  return null;
}

/**
 * Remove a chave global legada para não vazar a conta Google de um vendedor para outro.
 * Se pertencia ao usuário de tecnologia (id 1), migra para a chave individual dele.
 */
function cleanupLegacyGlobalKey(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed?.email?.includes("tecnologia") ||
        parsed?.email?.includes("dotus")
      ) {
        if (!localStorage.getItem(`${STORAGE_KEY}_1`)) {
          localStorage.setItem(`${STORAGE_KEY}_1`, raw);
        }
      } else if (
        parsed?.email?.includes("pf881230") ||
        parsed?.email?.includes("pedro")
      ) {
        if (!localStorage.getItem(`${STORAGE_KEY}_2`)) {
          localStorage.setItem(`${STORAGE_KEY}_2`, raw);
        }
      }
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
}

/** Chave de armazenamento individualizada por vendedor */
function userStorageKey(salesId: string | number): string {
  return `${STORAGE_KEY}_${salesId}`;
}

/**
 * Retorna a conta Google vinculada a um vendedor específico do CRM.
 * Se não houver conta salva para aquele vendedor, retorna null.
 */
export function getConnectedGoogleUserBySalesId(
  salesId: string | number,
): GoogleUser | null {
  cleanupLegacyGlobalKey();
  try {
    const key = userStorageKey(salesId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const user: GoogleUser = JSON.parse(raw);
    if (user.expiresAt && Date.now() > user.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

/**
 * Salva a conta Google vinculada a um vendedor específico do CRM.
 */
export function saveGoogleUserBySalesId(
  salesId: string | number,
  user: GoogleUser,
): void {
  cleanupLegacyGlobalKey();
  localStorage.setItem(userStorageKey(salesId), JSON.stringify(user));
}

/**
 * Desconecta a conta Google de um vendedor específico.
 */
export function disconnectGoogleUserBySalesId(salesId: string | number): void {
  cleanupLegacyGlobalKey();
  const key = userStorageKey(salesId);
  const current = getConnectedGoogleUserBySalesId(salesId);
  if (
    current?.accessToken &&
    (window as any).google?.accounts?.oauth2?.revoke
  ) {
    try {
      (window as any).google.accounts.oauth2.revoke(
        current.accessToken,
        () => {},
      );
    } catch {}
  }
  localStorage.removeItem(key);
}

/**
 * Retorna o ID do evento no Google Calendar vinculado a uma tarefa
 */
export function getGCalEventId(taskId: string | number): string | null {
  try {
    const raw = localStorage.getItem(GCAL_EVENTS_STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map[String(taskId)] || null;
  } catch {
    return null;
  }
}

/**
 * Salva o vínculo entre a tarefa do CRM e o evento do Google Calendar
 */
export function setGCalEventId(taskId: string | number, eventId: string): void {
  try {
    const raw = localStorage.getItem(GCAL_EVENTS_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(taskId)] = eventId;
    localStorage.setItem(GCAL_EVENTS_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

/**
 * Remove o vínculo de uma tarefa do CRM com o Google Calendar
 */
export function removeGCalEventId(taskId: string | number): void {
  try {
    const raw = localStorage.getItem(GCAL_EVENTS_STORAGE_KEY);
    if (!raw) return;
    const map = JSON.parse(raw);
    delete map[String(taskId)];
    localStorage.setItem(GCAL_EVENTS_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}
const GOOGLE_CLIENT_ID =
  import.meta.env.VITE_GOOGLE_CLIENT_ID ||
  "39179809017-1bm267kikq9242cp0o4heecv3fkr5d3h.apps.googleusercontent.com";

// Escopos necessários: Identidade + Envio de e-mail pelo Gmail + Google Agenda
const SCOPES = [
  "email",
  "profile",
  "https://www.googleapis.com/auth/gmail.send",
  "https://www.googleapis.com/auth/calendar.events",
].join(" ");

// Helper para codificar mensagem RFC 2822 em base64url
function base64UrlEncode(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

// Carrega o SDK oficial do Google Identity Services se ainda não estiver carregado
function loadGoogleScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).google?.accounts?.oauth2) {
      resolve();
      return;
    }

    const existing = document.getElementById("google-gsi-script");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", (e) => reject(e));
      return;
    }

    const script = document.createElement("script");
    script.id = "google-gsi-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
}

/**
 * Retorna os dados do Google vinculados ao usuário atualmente logado (ou ao salesId especificado).
 * NUNCA retorna os dados de outro usuário.
 */
export function getConnectedGoogleUser(
  salesId?: string | number | null,
): GoogleUser | null {
  cleanupLegacyGlobalKey();
  const targetId = salesId ?? getCurrentLoggedInSaleId();
  if (targetId != null) {
    return getConnectedGoogleUserBySalesId(targetId);
  }
  return null;
}

/**
 * Salva a sessão do usuário Google para o vendedor atualmente logado (ou especificado).
 */
export function saveGoogleUser(
  user: GoogleUser,
  salesId?: string | number | null,
): void {
  cleanupLegacyGlobalKey();
  const targetId = salesId ?? getCurrentLoggedInSaleId();
  if (targetId != null) {
    saveGoogleUserBySalesId(targetId, user);
  }
}

/**
 * Desconecta a integração do Google para o vendedor atual (ou especificado).
 */
export function disconnectGoogleUser(salesId?: string | number | null): void {
  cleanupLegacyGlobalKey();
  const targetId = salesId ?? getCurrentLoggedInSaleId();
  if (targetId != null) {
    disconnectGoogleUserBySalesId(targetId);
  }
}

/**
 * Inicia o fluxo OAuth 2.0 do Google solicitando os escopos do Gmail e Google Agenda
 */
export async function connectGoogleOAuth(
  onSuccess?: (user: GoogleUser) => void,
  onError?: (error: any) => void,
  salesId?: string | number | null,
): Promise<void> {
  const targetSalesId = salesId ?? getCurrentLoggedInSaleId();
  try {
    await loadGoogleScript();

    const google = (window as any).google;
    if (!google?.accounts?.oauth2) {
      throw new Error("SDK do Google não pôde ser inicializado.");
    }

    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: SCOPES,
      callback: async (response: any) => {
        if (response.error) {
          console.error("Erro na autenticação Google:", response);
          onError?.(response);
          return;
        }

        const accessToken = response.access_token;
        const expiresIn = response.expires_in || 3600;

        try {
          // Busca informações do perfil do usuário com o token
          const userinfoRes = await fetch(
            "https://www.googleapis.com/oauth2/v3/userinfo",
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );
          const userInfo = await userinfoRes.json();

          const googleUser: GoogleUser = {
            email: userInfo.email,
            name: userInfo.name || userInfo.email,
            picture: userInfo.picture,
            accessToken,
            connectedAt: new Date().toISOString(),
            expiresAt: Date.now() + expiresIn * 1000,
          };

          if (targetSalesId != null) {
            saveGoogleUserBySalesId(targetSalesId, googleUser);
          }
          // Vincula também automaticamente pelo e-mail caso corresponda aos usuários do sistema
          if (userInfo.email) {
            const emailLower = userInfo.email.toLowerCase();
            if (
              emailLower.includes("tecnologia") ||
              emailLower.includes("dotus")
            ) {
              saveGoogleUserBySalesId(1, googleUser);
            } else if (
              emailLower.includes("pf881230") ||
              emailLower.includes("pedro")
            ) {
              saveGoogleUserBySalesId(2, googleUser);
            }
          }
          onSuccess?.(googleUser);
        } catch (fetchErr) {
          console.error("Erro ao buscar perfil Google:", fetchErr);
          const fallbackUser: GoogleUser = {
            email: "dono@empresa.com.br",
            name: "Administrador / Dono",
            accessToken,
            connectedAt: new Date().toISOString(),
            expiresAt: Date.now() + expiresIn * 1000,
          };
          if (targetSalesId != null) {
            saveGoogleUserBySalesId(targetSalesId, fallbackUser);
          }
          onSuccess?.(fallbackUser);
        }
      },
    });

    client.requestAccessToken();
  } catch (err) {
    console.error("Falha ao abrir diálogo de autorização do Google:", err);
    onError?.(err);
  }
}

/**
 * Envia um e-mail de convite diretamente pelo Gmail do dono conectado via Gmail API.
 */
export async function sendGmailInvite(
  params: SendInviteParams,
): Promise<{ success: boolean; message: string; simulated?: boolean }> {
  const googleUser = getConnectedGoogleUser();

  const senderName =
    params.ownerName || googleUser?.name || "Administrador do CRM";
  const senderEmail =
    params.ownerEmail || googleUser?.email || "contato@empresa.com.br";
  const loginUrl = params.loginUrl || window.location.origin;

  const dealsRoleLabel =
    params.roleDeals === "admin"
      ? "Administrador dos negócios"
      : "Usuário comum de negócios";

  const globalRoleLabel =
    params.roleGlobal === "admin"
      ? "Administrador global"
      : "Usuário comum global";

  const subject = `Convite de acesso ao CRM DOTUS - Equipe de Vendas`;

  const htmlBody = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f4f4f5; margin: 0; padding: 24px; color: #18181b; }
    .container { max-width: 580px; margin: 0 auto; background: #ffffff; border-radius: 12px; border: 1px solid #e4e4e7; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
    .header { background: #09090b; padding: 32px 28px; text-align: center; color: #ffffff; }
    .header h1 { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.5px; }
    .header p { margin: 6px 0 0; font-size: 13px; color: #a1a1aa; }
    .content { padding: 32px 28px; }
    .greeting { font-size: 16px; font-weight: 600; margin-bottom: 12px; }
    .text { font-size: 14px; line-height: 1.6; color: #3f3f46; margin-bottom: 20px; }
    .roles-card { background: #fafafa; border: 1px solid #e4e4e7; border-radius: 8px; padding: 16px; margin: 20px 0; }
    .roles-card h4 { margin: 0 0 10px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #71717a; }
    .role-item { font-size: 13px; margin: 4px 0; color: #18181b; }
    .role-badge { display: inline-block; background: #e0e7ff; color: #3730a3; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 600; }
    .btn-container { text-align: center; margin: 32px 0 24px; }
    .btn { display: inline-block; background: #16a34a; color: #ffffff !important; text-decoration: none; padding: 14px 28px; border-radius: 8px; font-size: 14px; font-weight: 600; letter-spacing: 0.2px; }
    .footer { border-top: 1px solid #f4f4f5; padding: 20px 28px; font-size: 12px; color: #71717a; text-align: center; background: #fafafa; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>CRM DOTUS</h1>
      <p>Gestão Comercial & Inteligência de Vendas</p>
    </div>
    <div class="content">
      <div class="greeting">Olá, ${params.recipientName}!</div>
      <p class="text">
        <strong>${senderName}</strong> (${senderEmail}) convidou você para fazer parte do time no <strong>CRM DOTUS</strong>.
      </p>
      
      <div class="roles-card">
        <h4>Suas Permissões Atribuídas</h4>
        <div class="role-item">• <strong>Negócios (Deals):</strong> <span class="role-badge">${dealsRoleLabel}</span></div>
        <div class="role-item">• <strong>Recursos Globais:</strong> <span class="role-badge">${globalRoleLabel}</span></div>
      </div>

      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; margin: 20px 0;">
        <h4 style="margin: 0 0 10px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; color: #475569;">Suas Credenciais de Acesso</h4>
        <div style="font-size: 13px; color: #1e293b; margin: 6px 0;">
          <strong>E-mail:</strong> <code style="background: #e2e8f0; padding: 2px 6px; border-radius: 4px; font-size: 13px;">${params.to}</code>
        </div>
        ${
          params.tempPassword
            ? `
        <div style="font-size: 13px; color: #1e293b; margin: 6px 0;">
          <strong>Senha Provisória:</strong> <code style="background: #fee2e2; color: #991b1b; padding: 2px 6px; border-radius: 4px; font-size: 14px; font-weight: bold;">${params.tempPassword}</code>
        </div>
        <div style="font-size: 11px; color: #64748b; margin-top: 8px;">
          * Ao fazer seu primeiro login, você poderá alterar sua senha para uma de sua preferência acessando o seu Perfil/Configurações.
        </div>
        `
            : ""
        }
      </div>

      <p class="text">
        Clique no botão abaixo para ir para a tela de login do CRM:
      </p>

      <div class="btn-container">
        <a href="${loginUrl}/#/login" class="btn" target="_blank">Acessar CRM DOTUS (Fazer Login)</a>
      </div>

      <p class="text" style="font-size: 12px; color: #71717a;">
        Se você não esperava por este e-mail, pode desconsiderá-lo com segurança.
      </p>
    </div>
    <div class="footer">
      Mensagem enviada automaticamente a partir do Gmail de ${senderName} (${senderEmail}).
    </div>
  </div>
</body>
</html>
  `.trim();

  // Se não tiver Google User conectado no momento, realiza simulação amigável
  if (!googleUser || !googleUser.accessToken) {
    console.warn("[Gmail Integration] Simulação de envio de convite:", {
      from: senderEmail,
      to: params.to,
      subject,
    });
    return {
      success: true,
      simulated: true,
      message: `Convite simulado com sucesso de ${senderEmail} para ${params.to}. (Conecte seu Gmail para envio real via Google API).`,
    };
  }

  // Monta a mensagem MIME RFC 2822
  const mimeMessage = [
    `From: "${senderName}" <${googleUser.email}>`,
    `To: ${params.to}`,
    `Subject: =?utf-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`,
    `MIME-Version: 1.0`,
    `Content-Type: text/html; charset=UTF-8`,
    `Content-Transfer-Encoding: 8bit`,
    ``,
    htmlBody,
  ].join("\r\n");

  const raw = base64UrlEncode(mimeMessage);

  try {
    const res = await fetch(
      "https://gmail.googleapis.com/gmail/v1/users/me/messages/send",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${googleUser.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ raw }),
      },
    );

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      console.error("Erro ao enviar pelo Gmail API:", errData);
      throw new Error(
        errData?.error?.message || `Falha no envio (HTTP ${res.status})`,
      );
    }

    return {
      success: true,
      simulated: false,
      message: `E-mail de convite enviado com sucesso pelo Gmail (${googleUser.email}) para ${params.to}!`,
    };
  } catch (err: any) {
    console.error("Falha no envio via Gmail:", err);
    return {
      success: false,
      message: err?.message || "Não foi possível enviar o e-mail pelo Gmail.",
    };
  }
}

/**
 * Cria ou atualiza um evento diretamente no Google Agenda (Google Calendar)
 * com suporte à criação automática de salas no Google Meet
 */
export async function syncEventToGoogleCalendar(
  params: CalendarEventParams,
): Promise<{
  success: boolean;
  eventId?: string;
  htmlLink?: string;
  meetLink?: string;
  updated?: boolean;
  message: string;
  needsAuth?: boolean;
}> {
  const googleUser =
    params.salesId != null
      ? getConnectedGoogleUserBySalesId(params.salesId)
      : getConnectedGoogleUser();

  if (!googleUser || !googleUser.accessToken) {
    return {
      success: false,
      message:
        params.salesId != null
          ? "O usuário responsável não possui o Google Agenda conectado."
          : "Google Agenda não está conectada. Conecte sua conta Google no topo da tela.",
    };
  }

  // Identifica se já existe um evento no Google Calendar para esta tarefa
  const targetEventId =
    params.eventId || (params.taskId ? getGCalEventId(params.taskId) : null);

  const shouldCreateMeet =
    params.createMeetLink ||
    (params.location && params.location.includes("meet.google.com")) ||
    (!params.location &&
      (params.title.toLowerCase().includes("meet") ||
        params.description?.toLowerCase().includes("reuniao")));

  let eventDesc = params.notes
    ? `${params.notes}\n\n---\nAtividade no CRM Dotus (${params.type || "Tarefa"})`
    : params.description ||
      `Atividade no CRM Dotus (${params.type || "Tarefa"})`;
  if (
    params.location &&
    params.location.includes("meet.google.com") &&
    !eventDesc.includes(params.location)
  ) {
    eventDesc =
      `${eventDesc}\n\nLink do Google Meet: ${params.location}`.trim();
  }

  const eventPayload: any = {
    summary: params.title,
    description: eventDesc,
    location: params.location || "",
    start: {
      dateTime: params.startTime,
      timeZone: "America/Sao_Paulo",
    },
    end: {
      dateTime: params.endTime,
      timeZone: "America/Sao_Paulo",
    },
    attendees: params.attendees?.map((email) => ({ email })),
    reminders: {
      useDefault: true,
    },
  };

  if (shouldCreateMeet) {
    eventPayload.conferenceData = {
      createRequest: {
        requestId: `meet-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        conferenceSolutionKey: {
          type: "hangoutsMeet",
        },
      },
    };
  }

  try {
    // 1. Se já existe um ID associado, faz UPDATE (PATCH) no evento existente no Google Calendar
    if (targetEventId) {
      const patchRes = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events/${encodeURIComponent(targetEventId)}?conferenceDataVersion=1&sendUpdates=all`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${googleUser.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(eventPayload),
        },
      );

      if (patchRes.ok) {
        const updatedEvent = await patchRes.json();
        const meetUrl =
          updatedEvent.hangoutLink ||
          updatedEvent.conferenceData?.entryPoints?.find(
            (ep: any) => ep.entryPointType === "video",
          )?.uri ||
          undefined;

        if (params.taskId) {
          setGCalEventId(params.taskId, updatedEvent.id);
          if (updatedEvent.htmlLink) {
            setGCalHtmlLink(params.taskId, updatedEvent.htmlLink);
          }
        }
        return {
          success: true,
          eventId: updatedEvent.id,
          htmlLink: updatedEvent.htmlLink,
          meetLink: meetUrl,
          updated: true,
          message: meetUrl
            ? "Horário e sala do Google Meet atualizados com sucesso!"
            : "Horário atualizado com sucesso no Google Agenda!",
        };
      }

      // Se deu 404, o evento foi apagado no Google Calendar; continua abaixo para recriar (POST)
      if (patchRes.status === 401) {
        if (params.salesId != null) {
          disconnectGoogleUserBySalesId(params.salesId);
        }
        return {
          success: false,
          needsAuth: true,
          message:
            "A sessão do Google do responsável expirou ou as credenciais são inválidas.",
        };
      }

      if (patchRes.status !== 404) {
        const errData = await patchRes.json().catch(() => ({}));
        throw new Error(
          errData?.error?.message || `Erro HTTP ${patchRes.status}`,
        );
      }
    }

    // 2. Criação de novo evento com Google Meet (POST)
    const res = await fetch(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1&sendUpdates=all",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${googleUser.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(eventPayload),
      },
    );

    if (!res.ok) {
      if (res.status === 401) {
        if (params.salesId != null) {
          disconnectGoogleUserBySalesId(params.salesId);
        }
        return {
          success: false,
          needsAuth: true,
          message:
            "A sessão do Google do responsável expirou ou as credenciais são inválidas.",
        };
      }
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData?.error?.message || `Erro HTTP ${res.status}`);
    }

    const createdEvent = await res.json();
    const meetUrl =
      createdEvent.hangoutLink ||
      createdEvent.conferenceData?.entryPoints?.find(
        (ep: any) => ep.entryPointType === "video",
      )?.uri ||
      undefined;

    if (params.taskId) {
      setGCalEventId(params.taskId, createdEvent.id);
      if (createdEvent.htmlLink) {
        setGCalHtmlLink(params.taskId, createdEvent.htmlLink);
      }
    }

    return {
      success: true,
      eventId: createdEvent.id,
      htmlLink: createdEvent.htmlLink,
      meetLink: meetUrl,
      updated: false,
      message: meetUrl
        ? "Reunião sincronizada com sala do Google Meet criada!"
        : "Atividade sincronizada com sucesso no Google Agenda!",
    };
  } catch (err: any) {
    return {
      success: false,
      message: err?.message || "Falha ao sincronizar com Google Agenda.",
    };
  }
}

/**
 * Cria uma sala de reunião instantânea no Google Meet através do Google Calendar API
 */
export async function createGoogleMeetRoom(params?: {
  title?: string;
  startTime?: string;
  endTime?: string;
  taskId?: string | number;
  eventId?: string | null;
  salesId?: string | number | null;
  attendees?: string[];
}): Promise<{
  success: boolean;
  meetLink?: string;
  eventId?: string;
  message: string;
  needsAuth?: boolean;
}> {
  const googleUser =
    params?.salesId != null
      ? getConnectedGoogleUserBySalesId(params.salesId)
      : getConnectedGoogleUser();
  if (!googleUser || !googleUser.accessToken) {
    return {
      success: false,
      message:
        params?.salesId != null
          ? "O usuário responsável não possui o Google Agenda conectado para gerar o Meet."
          : "Google Agenda não está conectada.",
    };
  }

  const now = new Date();
  const start = params?.startTime || now.toISOString();
  const end =
    params?.endTime || new Date(now.getTime() + 60 * 60000).toISOString();
  const rawTitle = params?.title?.trim() || "Reunião Comercial - CRM DOTUS";
  const title = rawTitle.replace(/^(Reunião:\s*)+/i, "Reunião: ");

  const targetEventId =
    params?.eventId || (params?.taskId ? getGCalEventId(params.taskId) : null);

  // 1. Se já existe um evento vinculado no Google Calendar, ATUALIZA (PATCH) adicionando o Meet
  if (targetEventId) {
    try {
      const patchBody: any = {
        summary: title,
        start: { dateTime: start, timeZone: "America/Sao_Paulo" },
        end: { dateTime: end, timeZone: "America/Sao_Paulo" },
        conferenceData: {
          createRequest: {
            requestId: `meet-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            conferenceSolutionKey: {
              type: "hangoutsMeet",
            },
          },
        },
      };
      if (params?.attendees && params.attendees.length > 0) {
        patchBody.attendees = params.attendees.map((email) => ({ email }));
      }

      const patchRes = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events/${encodeURIComponent(targetEventId)}?conferenceDataVersion=1&sendUpdates=all`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${googleUser.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(patchBody),
        },
      );

      if (patchRes.status === 401) {
        if (params?.salesId != null) {
          disconnectGoogleUserBySalesId(params.salesId);
        }
        return {
          success: false,
          needsAuth: true,
          message:
            "A sessão do Google do proprietário expirou ou as credenciais são inválidas.",
        };
      }

      if (patchRes.ok) {
        const updated = await patchRes.json();
        const meetLink =
          updated.hangoutLink ||
          updated.conferenceData?.entryPoints?.find(
            (ep: any) => ep.entryPointType === "video",
          )?.uri ||
          null;

        if (meetLink) {
          if (params?.taskId) {
            setGCalEventId(params.taskId, updated.id);
            if (updated.htmlLink)
              setGCalHtmlLink(params.taskId, updated.htmlLink);
          }
          return {
            success: true,
            meetLink,
            eventId: updated.id,
            message: "Sala do Google Meet vinculada ao compromisso existente!",
          };
        }
      }
    } catch (patchErr) {
      console.warn(
        "Tentativa de atualizar evento existente falhou, tentando recriar:",
        patchErr,
      );
    }
  }

  // 2. Se não existe ou não foi encontrado, cria um novo evento com Google Meet
  const payload: any = {
    summary: title,
    description: "Reunião criada via CRM DOTUS com sala do Google Meet",
    start: {
      dateTime: start,
      timeZone: "America/Sao_Paulo",
    },
    end: {
      dateTime: end,
      timeZone: "America/Sao_Paulo",
    },
    conferenceData: {
      createRequest: {
        requestId: `meet-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        conferenceSolutionKey: {
          type: "hangoutsMeet",
        },
      },
    },
  };
  if (params?.attendees && params.attendees.length > 0) {
    payload.attendees = params.attendees.map((email) => ({ email }));
  }

  try {
    const res = await fetch(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events?conferenceDataVersion=1&sendUpdates=all",
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${googleUser.accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (!res.ok) {
      if (res.status === 401) {
        if (params?.salesId != null) {
          disconnectGoogleUserBySalesId(params.salesId);
        }
        return {
          success: false,
          needsAuth: true,
          message:
            "A sessão do Google do proprietário expirou ou as credenciais são inválidas.",
        };
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || `Erro HTTP ${res.status}`);
    }

    const data = await res.json();
    const meetLink =
      data.hangoutLink ||
      data.conferenceData?.entryPoints?.find(
        (ep: any) => ep.entryPointType === "video",
      )?.uri ||
      null;

    if (!meetLink) {
      return {
        success: false,
        message: "Não foi possível obter o link do Google Meet.",
      };
    }

    if (params?.taskId) {
      setGCalEventId(params.taskId, data.id);
      if (data.htmlLink) setGCalHtmlLink(params.taskId, data.htmlLink);
    }

    return {
      success: true,
      meetLink,
      eventId: data.id,
      message: "Sala do Google Meet gerada com sucesso!",
    };
  } catch (err: any) {
    console.error("Erro ao gerar Google Meet:", err);
    return {
      success: false,
      message: err?.message || "Erro ao conectar com Google Meet.",
    };
  }
}

const GCAL_LINKS_STORAGE_KEY = "dotus_crm_gcal_links";

export function getGCalHtmlLink(taskId: string | number): string | null {
  try {
    const raw = localStorage.getItem(GCAL_LINKS_STORAGE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw);
    return map[String(taskId)] || null;
  } catch {
    return null;
  }
}

export function setGCalHtmlLink(taskId: string | number, link: string): void {
  try {
    const raw = localStorage.getItem(GCAL_LINKS_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(taskId)] = link;
    localStorage.setItem(GCAL_LINKS_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

/**
 * Exclui um evento do Google Agenda quando a atividade for excluída no CRM
 */
export async function deleteEventFromGoogleCalendar(
  target:
    | string
    | number
    | {
        taskId?: string | number;
        eventId?: string | null;
        salesId?: string | number | null;
      },
): Promise<{ success: boolean; message: string }> {
  const targetSalesId =
    typeof target === "object" && target !== null ? target.salesId : undefined;
  const googleUser =
    targetSalesId != null
      ? getConnectedGoogleUserBySalesId(targetSalesId)
      : getConnectedGoogleUser();
  if (!googleUser || !googleUser.accessToken) {
    return { success: false, message: "Google Agenda não conectada" };
  }

  let eventId: string | null = null;
  let taskId: string | number | null = null;

  if (typeof target === "object" && target !== null) {
    eventId = target.eventId || null;
    taskId = target.taskId || null;
  } else {
    taskId = target;
  }

  if (!eventId && taskId) {
    eventId = getGCalEventId(taskId);
  }
  if (!eventId && typeof target === "string" && !target.match(/^\d+$/)) {
    eventId = target;
  }

  if (taskId) {
    removeGCalEventId(taskId);
  }

  if (!eventId) {
    return { success: false, message: "ID do evento não encontrado" };
  }

  try {
    const res = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${encodeURIComponent(eventId)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${googleUser.accessToken}`,
        },
      },
    );
    if (
      res.ok ||
      res.status === 204 ||
      res.status === 404 ||
      res.status === 410
    ) {
      return { success: true, message: "Evento removido do Google Agenda" };
    }
    return {
      success: false,
      message: `Erro ao excluir do Google Agenda (${res.status})`,
    };
  } catch {
    return { success: false, message: "Erro ao excluir do Google Agenda" };
  }
}

/**
 * Helper unificado para sincronizar qualquer Task com o Google Agenda (criação ou atualização de dia/horário)
 */
export async function syncTaskWithGoogleCalendar(task: {
  id?: string | number;
  sales_id?: string | number | null;
  eventId?: string | null;
  google_calendar_event_id?: string | null;
  text: string;
  type?: string;
  notes?: string | null;
  data_inicio?: string | null;
  due_date?: string | null;
  data_fim?: string | null;
  link_chamada?: string | null;
  createMeetLink?: boolean;
  attendees?: string[];
}): Promise<{
  success: boolean;
  eventId?: string;
  htmlLink?: string;
  meetLink?: string;
  updated?: boolean;
  message: string;
  needsAuth?: boolean;
  skipped?: boolean;
}> {
  // Verifica se o tipo de atividade está selecionado nas preferências de sincronização do perfil
  if (!isTaskTypeAllowedForGCalSync(task.type, task.sales_id)) {
    return {
      success: false,
      skipped: true,
      message: `Atividade do tipo "${task.type || "tarefa"}" não está configurada para sincronizar com o Google Agenda no perfil.`,
    };
  }

  const googleUser =
    task.sales_id != null
      ? getConnectedGoogleUserBySalesId(task.sales_id)
      : getConnectedGoogleUser();
  if (!googleUser) {
    return {
      success: false,
      message:
        task.sales_id != null
          ? "O usuário responsável não possui o Google Agenda conectado."
          : "Google Agenda não conectada",
    };
  }

  const startIso =
    task.data_inicio || task.due_date || new Date().toISOString();
  let endIso = task.data_fim;
  if (!endIso) {
    endIso = new Date(
      new Date(startIso).getTime() + 60 * 60 * 1000,
    ).toISOString();
  }

  const shouldMeet =
    task.createMeetLink ||
    task.type === "reuniao" ||
    (task.link_chamada ? task.link_chamada.includes("meet.google.com") : false);

  const existingGcalId =
    task.google_calendar_event_id ||
    task.eventId ||
    (task.id ? getGCalEventId(task.id) : null);

  const cleanTitle = (task.text || "").replace(/^(Reunião:\s*)+/i, "Reunião: ");

  return syncEventToGoogleCalendar({
    taskId: task.id,
    salesId: task.sales_id,
    eventId: existingGcalId || undefined,
    title: cleanTitle,
    notes: task.notes,
    type: task.type,
    description:
      task.notes || `Atividade no CRM Dotus (${task.type || "Tarefa"})`,
    startTime: startIso,
    endTime: endIso,
    location: task.link_chamada || undefined,
    createMeetLink: shouldMeet,
    attendees: task.attendees,
  });
}

const TASK_ATTENDEES_STORAGE_KEY = "dotus_crm_task_attendees";

export function getTaskAttendees(taskId: string | number): string[] {
  try {
    const raw = localStorage.getItem(TASK_ATTENDEES_STORAGE_KEY);
    if (!raw) return [];
    const map = JSON.parse(raw);
    return Array.isArray(map[String(taskId)]) ? map[String(taskId)] : [];
  } catch {
    return [];
  }
}

export function setTaskAttendees(
  taskId: string | number,
  attendees: string[],
): void {
  try {
    const raw = localStorage.getItem(TASK_ATTENDEES_STORAGE_KEY);
    const map = raw ? JSON.parse(raw) : {};
    map[String(taskId)] = attendees;
    localStorage.setItem(TASK_ATTENDEES_STORAGE_KEY, JSON.stringify(map));
  } catch {}
}

/**
 * Busca eventos diretamente do Google Agenda em um intervalo de tempo
 */
export async function fetchGoogleCalendarEvents(
  timeMin: Date,
  timeMax: Date,
  salesId?: string | number | null,
): Promise<any[]> {
  const googleUser =
    salesId != null
      ? getConnectedGoogleUserBySalesId(salesId)
      : getConnectedGoogleUser();
  if (!googleUser || !googleUser.accessToken) {
    return [];
  }

  try {
    const url = new URL(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events",
    );
    url.searchParams.set("timeMin", timeMin.toISOString());
    url.searchParams.set("timeMax", timeMax.toISOString());
    url.searchParams.set("singleEvents", "true");
    url.searchParams.set("showDeleted", "true");
    url.searchParams.set("orderBy", "startTime");

    const res = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${googleUser.accessToken}`,
      },
    });

    if (!res.ok) {
      return [];
    }

    const data = await res.json();
    return data.items || [];
  } catch (err) {
    console.error("Erro ao buscar eventos do Google Agenda:", err);
    return [];
  }
}

const activeSyncSalesKeys = new Set<string>();
const inFlightEventIds = new Set<string>();

/**
 * Sincronização em 2 Vias Completa (Google Agenda 🔄 CRM):
 * 1. Atualizações e novos eventos criados no Google Agenda são refletidos no CRM.
 * 2. Eventos excluídos no Google Agenda são automaticamente excluídos do CRM.
 */
export async function syncGoogleCalendarToCrm(
  dataProvider: any,
  options?: {
    timeMin?: Date;
    timeMax?: Date;
    salesId?: string | number | null;
  },
): Promise<{
  success: boolean;
  imported: number;
  updated: number;
  deleted: number;
  message: string;
}> {
  const googleUser =
    options?.salesId != null
      ? getConnectedGoogleUserBySalesId(options.salesId)
      : getConnectedGoogleUser();
  if (!googleUser) {
    return {
      success: false,
      imported: 0,
      updated: 0,
      deleted: 0,
      message: "Google Agenda não está conectada para este usuário.",
    };
  }

  const syncKey = options?.salesId != null ? String(options.salesId) : "global";
  if (activeSyncSalesKeys.has(syncKey)) {
    return {
      success: true,
      imported: 0,
      updated: 0,
      deleted: 0,
      message: "Sincronização já em andamento...",
    };
  }
  activeSyncSalesKeys.add(syncKey);

  const now = new Date();
  const timeMin =
    options?.timeMin ||
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - 15);
  const timeMax =
    options?.timeMax ||
    new Date(now.getFullYear(), now.getMonth(), now.getDate() + 45);

  try {
    const googleEvents = await fetchGoogleCalendarEvents(
      timeMin,
      timeMax,
      options?.salesId,
    );
    if (!googleEvents || googleEvents.length === 0) {
      return {
        success: true,
        imported: 0,
        updated: 0,
        deleted: 0,
        message:
          "Nenhum compromisso encontrado no Google Agenda para o período.",
      };
    }

    // Busca as tarefas do CRM para cruzar os dados
    const crmTasksRes = await dataProvider.getList("tasks", {
      pagination: { page: 1, perPage: 1500 },
      sort: { field: "data_inicio", order: "ASC" },
      filter: {},
    });
    const crmTasks: any[] = crmTasksRes.data || [];

    const activeGoogleEvents = googleEvents.filter(
      (e) => e.status !== "cancelled",
    );
    const activeGoogleEventIds = new Set(
      activeGoogleEvents.map((e) => String(e.id)),
    );
    const cancelledGoogleEventIds = new Set(
      googleEvents
        .filter((e) => e.status === "cancelled")
        .map((e) => String(e.id)),
    );

    let imported = 0;
    let updated = 0;
    let deleted = 0;

    // 1. SINCRONIZAÇÃO DE EXCLUSÃO (Google Agenda ➡️ CRM):
    // Se o evento foi cancelado ou excluído no Google, remove a atividade correspondente no CRM.
    // CRÍTICO: Só avalia exclusão para tarefas pertencentes a este usuário específico (salesId)!
    for (const cTask of crmTasks) {
      if (options?.salesId != null) {
        if (
          !cTask.sales_id ||
          String(cTask.sales_id) !== String(options.salesId)
        ) {
          continue;
        }
      }

      const gcalId = cTask.google_calendar_event_id || getGCalEventId(cTask.id);
      if (!gcalId) continue;

      const taskDateStr = cTask.data_inicio || cTask.due_date;
      if (!taskDateStr) continue;
      const taskTime = new Date(taskDateStr).getTime();
      if (taskTime < timeMin.getTime() || taskTime > timeMax.getTime())
        continue;

      const isCancelledInGoogle = cancelledGoogleEventIds.has(String(gcalId));
      const isMissingFromGoogle = !activeGoogleEventIds.has(String(gcalId));

      if (isCancelledInGoogle || isMissingFromGoogle) {
        try {
          await dataProvider.delete("tasks", {
            id: cTask.id,
            previousData: cTask,
          });
          removeGCalEventId(cTask.id);
          deleted++;
        } catch (delErr) {
          console.error(
            "Erro ao deletar tarefa excluída no Google Agenda:",
            delErr,
          );
        }
      }
    }

    for (const gEvent of googleEvents) {
      // Ignora eventos sem data ou cancelados
      if (gEvent.status === "cancelled") continue;
      const startDateTime = gEvent.start?.dateTime || gEvent.start?.date;
      if (!startDateTime) continue;

      const endDateTime = gEvent.end?.dateTime || gEvent.end?.date || null;
      const eventTitle =
        (gEvent.summary || "").trim() || "(Sem título - Google Agenda)";
      const hangoutLink = gEvent.hangoutLink || gEvent.location || null;

      // 1. Procura se já existe uma atividade vinculada por ID do Google direto ou ID local
      let existingTask = crmTasks.find((t) => {
        if (
          options?.salesId != null &&
          t.sales_id != null &&
          String(t.sales_id) !== String(options.salesId)
        ) {
          return false;
        }
        return (
          (t.google_calendar_event_id &&
            String(t.google_calendar_event_id) === String(gEvent.id)) ||
          String(getGCalEventId(t.id) || "") === String(gEvent.id)
        );
      });

      // 2. Se não encontrou por ID direto, faz casamento inteligente:
      if (!existingTask) {
        const eventTime = new Date(startDateTime).getTime();
        const cleanNorm = (s: string) =>
          s
            .toLowerCase()
            .replace(/^(reunião:\s*)+/i, "")
            .replace(/[^a-z0-9]/gi, "")
            .trim();
        const gNorm = cleanNorm(eventTitle);

        existingTask = crmTasks.find((t) => {
          if (
            options?.salesId != null &&
            t.sales_id != null &&
            String(t.sales_id) !== String(options.salesId)
          ) {
            return false;
          }
          const taskDateStr = t.data_inicio || t.due_date;
          if (!taskDateStr) return false;
          const taskTime = new Date(taskDateStr).getTime();
          if (isNaN(taskTime)) return false;
          const diffMinutes = Math.abs(taskTime - eventTime) / 60000;

          // Mesma sala de Google Meet
          if (
            hangoutLink &&
            t.link_chamada &&
            (t.link_chamada === hangoutLink ||
              hangoutLink.includes(t.link_chamada) ||
              t.link_chamada.includes(hangoutLink))
          ) {
            return true;
          }

          // Mesmo horário (+/- 5 minutos)
          if (diffMinutes <= 5) {
            const tNorm = cleanNorm(t.text || "");
            if (
              !tNorm ||
              !gNorm ||
              tNorm === gNorm ||
              tNorm.includes(gNorm) ||
              gNorm.includes(tNorm)
            ) {
              return true;
            }
          }

          // Mesmo dia (+/- 60 minutos) com títulos idênticos
          if (diffMinutes <= 60 && gNorm.length > 2) {
            const tNorm = cleanNorm(t.text || "");
            return tNorm === gNorm;
          }

          return false;
        });

        // Se casou, vincula permanentemente o ID do Google a essa tarefa existente!
        if (existingTask) {
          setGCalEventId(existingTask.id, gEvent.id);
          if (gEvent.htmlLink)
            setGCalHtmlLink(existingTask.id, gEvent.htmlLink);
        }
      }

      if (existingTask) {
        // Verifica se a data, horário ou título mudaram no Google Agenda
        const currentStart = existingTask.data_inicio || existingTask.due_date;
        const currentStartTime = currentStart
          ? new Date(currentStart).getTime()
          : 0;
        const newStartTime = new Date(startDateTime).getTime();
        const startDiff = Math.abs(currentStartTime - newStartTime) > 30000; // mais de 30 segundos de diferença

        const cleanEventTitle = eventTitle.replace(
          /^(Reunião:\s*)+/i,
          "Reunião: ",
        );
        const titleDiff =
          cleanEventTitle &&
          existingTask.text !== cleanEventTitle &&
          !existingTask.text.includes(cleanEventTitle) &&
          !cleanEventTitle.includes(existingTask.text);

        if (startDiff || titleDiff) {
          const newStartIso = new Date(startDateTime).toISOString();
          const newEndIso = endDateTime
            ? new Date(endDateTime).toISOString()
            : new Date(
                new Date(startDateTime).getTime() + 60 * 60 * 1000,
              ).toISOString();

          const finalTitle = titleDiff ? cleanEventTitle : existingTask.text;

          try {
            await dataProvider.update("tasks", {
              id: existingTask.id,
              data: {
                ...existingTask,
                text: finalTitle,
                data_inicio: newStartIso,
                due_date: newStartIso,
                data_fim: newEndIso,
                link_chamada: hangoutLink || existingTask.link_chamada,
                google_calendar_event_id: gEvent.id,
                google_calendar_html_link:
                  gEvent.htmlLink || existingTask.google_calendar_html_link,
              },
              previousData: existingTask,
            });
          } catch {
            await dataProvider.update("tasks", {
              id: existingTask.id,
              data: {
                ...existingTask,
                text: finalTitle,
                data_inicio: newStartIso,
                due_date: newStartIso,
                data_fim: newEndIso,
                link_chamada: hangoutLink || existingTask.link_chamada,
              },
              previousData: existingTask,
            });
          }
          setGCalEventId(existingTask.id, gEvent.id);
          if (gEvent.htmlLink)
            setGCalHtmlLink(existingTask.id, gEvent.htmlLink);
          updated++;
        }
      } else {
        // Se for apenas o placeholder genérico e não estiver vinculado a uma tarefa salva, não importa
        if (
          eventTitle === "Reunião Comercial - CRM DOTUS" ||
          eventTitle === "Reunião Comercial"
        ) {
          continue;
        }

        // Atividade criada diretamente no Google Agenda (ex: celular) -> importa para o CRM
        inFlightEventIds.add(String(gEvent.id));
        let newTaskRes;
        try {
          newTaskRes = await dataProvider.create("tasks", {
            data: {
              text: eventTitle,
              type: "reuniao",
              prioridade: "media",
              sales_id: options?.salesId || undefined,
              data_inicio: new Date(startDateTime).toISOString(),
              due_date: new Date(startDateTime).toISOString(),
              data_fim: endDateTime
                ? new Date(endDateTime).toISOString()
                : null,
              link_chamada: hangoutLink,
              google_calendar_event_id: gEvent.id,
              google_calendar_html_link: gEvent.htmlLink || null,
            },
          });
        } catch {
          newTaskRes = await dataProvider.create("tasks", {
            data: {
              text: eventTitle,
              type: "reuniao",
              prioridade: "media",
              data_inicio: new Date(startDateTime).toISOString(),
              due_date: new Date(startDateTime).toISOString(),
              data_fim: endDateTime
                ? new Date(endDateTime).toISOString()
                : null,
              link_chamada: hangoutLink,
            },
          });
        } finally {
          setTimeout(() => inFlightEventIds.delete(String(gEvent.id)), 10000);
        }

        if (newTaskRes?.data?.id) {
          setGCalEventId(newTaskRes.data.id, gEvent.id);
          if (gEvent.htmlLink)
            setGCalHtmlLink(newTaskRes.data.id, gEvent.htmlLink);
          crmTasks.push(newTaskRes.data);
        }
        imported++;
      }
    }

    const parts = [];
    if (imported > 0) parts.push(`${imported} nova(s)`);
    if (updated > 0) parts.push(`${updated} atualizada(s)`);
    if (deleted > 0) parts.push(`${deleted} excluída(s)`);

    const summaryMsg =
      parts.length > 0
        ? `Sincronização concluída! ${parts.join(", ")}.`
        : "Agenda sincronizada e atualizada!";

    return {
      success: true,
      imported,
      updated,
      deleted,
      message: summaryMsg,
    };
  } catch (err: any) {
    console.error("Erro na sincronização 2 vias:", err);
    return {
      success: false,
      imported: 0,
      updated: 0,
      deleted: 0,
      message: err?.message || "Erro ao sincronizar com Google Agenda.",
    };
  } finally {
    const syncKey =
      options?.salesId != null ? String(options.salesId) : "global";
    activeSyncSalesKeys.delete(syncKey);
  }
}
